import { Link } from "react-router-dom";
import { MapPin, Phone, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { BUSINESS, SERVICE_AREAS } from "@/data/site";

const NOLA_IMG = "https://images.unsplash.com/photo-1586974325246-05d48d665f24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxuZXclMjBvcmxlYW5zJTIwc2t5bGluZXxlbnwwfHx8fDE3ODI3NDkxODd8MA&ixlib=rb-4.1.0&q=85";

export default function ServiceAreaPage() {
  return (
    <div data-testid="service-area-page">
      {/* Hero */}
      <section className="relative">
        <div className="ji-container pt-16 md:pt-24 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="ji-overline mb-5">Service Area</div>
            <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight">
              From the bayou to<br />
              the capitol —<br />
              <span className="text-[var(--ji-accent)] italic">we cover it.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-[var(--ji-muted)] leading-relaxed">
              {BUSINESS.serviceAreaLong}. If you're inside the I-10 corridor between
              New Orleans and Baton Rouge, you're in our backyard.
            </p>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="overflow-hidden border border-[var(--ji-border)]" style={{ borderRadius: 4 }}>
              <img src={NOLA_IMG} alt="Southern Louisiana skyline" loading="eager" fetchpriority="high" decoding="async" className="w-full h-[380px] md:h-[480px] object-cover" data-testid="service-area-image" />
            </div>
            <div className="absolute -bottom-5 -right-5 bg-[var(--ji-primary)] text-white p-5 shadow-xl hidden md:flex flex-col" style={{ borderRadius: 4 }}>
              <span className="text-xs uppercase tracking-[0.2em] opacity-80">I-10 Corridor</span>
              <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold tracking-tight mt-1">~80 mi</span>
            </div>
          </div>
        </div>
      </section>

      {/* Map illustration */}
      <section className="ji-container pb-16">
        <div className="ji-card p-8 md:p-12" style={{ borderRadius: 4 }} data-testid="service-area-route">
          <div className="ji-overline mb-6">Primary Corridor</div>
          <div className="relative">
            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-[var(--ji-accent)] via-[var(--ji-primary)] to-[var(--ji-accent)]" />
            <div className="relative grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { city: "Baton Rouge", note: "West end" },
                { city: "Gonzales", note: "" },
                { city: "LaPlace", note: "" },
                { city: "New Orleans", note: "East end" },
              ].map((c, i) => (
                <div key={c.city} className="flex flex-col items-center text-center bg-[var(--ji-bg)] px-3 py-4 border border-[var(--ji-border)]" style={{ borderRadius: 4 }}>
                  <MapPin size={22} weight="duotone" className="text-[var(--ji-accent)]" />
                  <div style={{ fontFamily: "Cabinet Grotesk" }} className="mt-2 text-lg font-bold tracking-tight">{c.city}</div>
                  {c.note && <div className="text-xs text-[var(--ji-muted)] mt-0.5">{c.note}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* All cities */}
      <section className="bg-[var(--ji-surface-alt)] border-y border-[var(--ji-border)]">
        <div className="ji-container py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="ji-overline mb-4">Cities served</div>
              <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-4xl md:text-5xl font-bold tracking-tight">
                And every parish in between.
              </h2>
              <p className="mt-4 text-[var(--ji-muted)] leading-relaxed">
                Don't see your town? Call us — if you're within reach, we'll come.
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {SERVICE_AREAS.map((a) => (
                <div key={a} data-testid={`area-${a.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-2 px-4 py-3 bg-[var(--ji-bg)] border border-[var(--ji-border)] text-sm" style={{ borderRadius: 4 }}>
                  <CheckCircle size={14} weight="duotone" className="text-[var(--ji-accent)] flex-shrink-0" />
                  {a}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ji-container py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {BUSINESS.phones.map((p) => (
            <a key={p.tel} href={`tel:${p.tel}`} data-testid={`area-cta-call-${p.label.toLowerCase().replace(/\s+/g, "-")}`} className="ji-card flex items-center justify-between p-8 group" style={{ borderRadius: 4 }}>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[var(--ji-accent)]">{p.label} Line</div>
                <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold tracking-tight mt-1">{p.display}</div>
              </div>
              <Phone size={28} weight="bold" className="text-[var(--ji-primary)]" />
            </a>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/contact" data-testid="area-cta-request" className="ji-btn-primary inline-flex items-center gap-2 px-6 h-14 text-base font-semibold" style={{ borderRadius: 4 }}>
            Or request service online <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
